/**
 * Cross-tenant "all users" view — ROOT only (Phase 8, Q2 gap-fill).
 *
 * Read-only listing of every person on the platform and, per person, the
 * tenants they belong to with the roles they hold there. Grants/edits live on
 * the Access Control and Platform tabs — this screen only surfaces the picture.
 * Gated PLATFORM_USER_MANAGE, hidden for non-ROOT (see AuthedRoutes).
 */

import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, IconButton, Tooltip, TextField, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import { listAllPlatformUsers } from '../../../utilityFunctions/PlatformAdminUtil';

export default function PlatformUsersScreen() {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    const res = await listAllPlatformUsers();
    setLoading(false);
    if (!res.ok) { setError(res.error || 'Could not load users'); return; }
    setError(null);
    setUsers(Array.isArray(res.data) ? res.data : []);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const q = filter.trim().toLowerCase();
  const shown = !q ? users : users.filter((u) => {
    const hay = `${u.username || ''} ${u.email || ''} ${(u.memberships || [])
      .map((m) => `${m.tenantSlug || ''} ${m.tenantDisplayName || ''}`).join(' ')}`.toLowerCase();
    return hay.indexOf(q) !== -1;
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <PeopleIcon sx={{ color: Colors.primary }} />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>All Users</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every person across every tenant, with the tenants they belong to and the roles they hold in each.
        Read-only — manage grants from Access Control.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        size="small" fullWidth placeholder="Filter by user, email or tenant…"
        value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ mb: 2 }} />

      {loading ? (
        <CustomLoader />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tenants &amp; roles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No users to show.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : shown.map((u) => (
                <TableRow key={u.username} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{u.username || '—'}</TableCell>
                  <TableCell>{u.email || '—'}</TableCell>
                  <TableCell>
                    {u.enabled === false || u.accountNonLocked === false
                      ? <Chip size="small" color="warning" label="Disabled" />
                      : <Chip size="small" color="success" label="Active" />}
                  </TableCell>
                  <TableCell>
                    {(!u.memberships || u.memberships.length === 0) ? (
                      <Typography variant="caption" color="text.secondary">No memberships</Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {u.memberships.map((m, i) => (
                          <Box key={`${u.username}-${m.tenantSlug || i}`} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              size="small" variant="outlined"
                              label={m.tenantDisplayName || m.tenantSlug || 'tenant'}
                              color={m.membershipStatus === 'ACTIVE' ? 'default' : 'warning'} />
                            {(m.roles || []).map((r) => (
                              <Chip key={`${u.username}-${m.tenantSlug || i}-${r}`} size="small" label={r} sx={{ bgcolor: `${Colors.primary}18` }} />
                            ))}
                            {m.membershipStatus && m.membershipStatus !== 'ACTIVE' && (
                              <Typography variant="caption" color="text.secondary">({m.membershipStatus})</Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
